/**
 * =============================================================================
 * Hook: useProgramTermsForAffiliate
 * Versão: 1.0.0 (2026-05-02)
 *
 * Descrição:
 *   Carrega o conteúdo do modelo de contrato vinculado a um Programa de
 *   Indicação (Termos do Programa) via RPC `get_program_terms_for_affiliate`.
 *
 *   - Espelho de useAffiliateCampaignTerms.
 *   - SECURITY DEFINER no banco valida que o usuário autenticado é um
 *     afiliado com membership ativa no programa.
 *   - Retorna o mesmo shape consumido por <ContractFullPreview/>.
 *   - Habilitado on-demand (apenas quando o dialog de termos abrir).
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const log = createLogger('useProgramTermsForAffiliate');

export interface ProgramTermsData {
  template_id: string;
  template_name: string;
  content_html: string;
  version: number;
  background_image_path: string | null;
  show_page_numbers: boolean;
  page_number_position: string | null;
  bg_margin_top_mm: number | null;
  bg_margin_bottom_mm: number | null;
  pagination_y_offset_mm: number | null;
  cover_pdf_path: string | null;
  base_pdf_path: string | null;
  base_pdf_position: 'before' | 'after';
}

export function useProgramTermsForAffiliate(
  programId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['affiliate-program-terms', programId],
    enabled: enabled && !!programId,
    staleTime: 60_000,
    queryFn: async (): Promise<ProgramTermsData | null> => {
      const { data, error } = await supabase.rpc(
        // Tipagem `as never` enquanto types.ts não regenera para incluir a nova função
        'get_program_terms_for_affiliate' as never,
        { p_program_id: programId } as never,
      );
      if (error) {
        log.error('Erro ao carregar termos do programa', error);
        throw error;
      }
      const raw = data as unknown;
      const row = (Array.isArray(raw) ? raw[0] : raw) as
        | Partial<ProgramTermsData>
        | null
        | undefined;
      if (!row) return null;
      const rawPos = (row as { base_pdf_position?: string }).base_pdf_position;
      const basePos: 'before' | 'after' = rawPos === 'after' ? 'after' : 'before';
      return {
        template_id: String(row.template_id ?? ''),
        template_name: row.template_name ?? 'Termos',
        content_html: row.content_html ?? '',
        version: Number(row.version ?? 1),
        background_image_path: row.background_image_path ?? null,
        show_page_numbers: !!row.show_page_numbers,
        page_number_position: row.page_number_position ?? null,
        bg_margin_top_mm: row.bg_margin_top_mm ?? null,
        bg_margin_bottom_mm: row.bg_margin_bottom_mm ?? null,
        pagination_y_offset_mm: row.pagination_y_offset_mm ?? null,
        cover_pdf_path:
          (row as { cover_pdf_path?: string | null }).cover_pdf_path ?? null,
        base_pdf_path:
          (row as { base_pdf_path?: string | null }).base_pdf_path ?? null,
        base_pdf_position: basePos,
      };
    },
  });
}
